import React, { useEffect, useState } from 'react';
import { departmentApi } from '@/features/workspace/api';
import { CheckIcon } from './DepartmentIcons';
import { useToast } from '@/hooks/useToast';

export const DepartmentEventsView: React.FC = () => {
  const { notify } = useToast();
  const [events, setEvents] = useState<any[]>([]);
  const [, setLoading] = useState<boolean>(true);
  const [title, setTitle] = useState<string>('');
  const [eventType, setEventType] = useState<string>('Встреча');
  const [eventDate, setEventDate] = useState<string>('2026-08-25 15:00');
  const [participants, setParticipants] = useState<string>('Сотрудники кафедры');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const res = await departmentApi.events();
      setEvents(res);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      setSubmitting(true);
      await departmentApi.createEvent({
        title: title.trim(),
        event_type: eventType,
        event_date: eventDate,
        participants,
      });
      setTitle('');
      setToastMessage('Событие создано, приглашение подготовлено');
      setTimeout(() => setToastMessage(null), 3500);
      fetchEvents();
    } catch {
      notify('Ошибка при создании события', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleConfirm = async (eventId: string) => {
    try {
      const updated = await departmentApi.toggleConfirmEvent(eventId);
      setEvents((prev) => prev.map((e) => (e.id === eventId ? updated : e)));
    } catch {
      notify('Не удалось изменить статус события', 'error');
    }
  };

  const totalEvents = events.length;
  const confirmedCount = events.filter((e) => e.status === 'CONFIRMED').length;
  const plannedCount = events.filter((e) => e.status === 'PLANNED').length;
  const typesCount = Array.from(new Set(events.map((e) => e.event_type))).length || 2;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-6">
        <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
          Canonical Event Workflow
        </span>
        <h2 className="text-2xl font-bold text-white tracking-tight mt-1">
          Встреча / дежурство
        </h2>
        <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-4xl">
          Тот же рабочий смысл, что в кабинете владельца: событие, получатели, приглашение, статус и привязка к сроку.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">События</span>
            <div className="text-3xl font-extrabold text-white mt-1">{totalEvents}</div>
          </div>
          <div className="p-4 rounded-xl border border-emerald-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Подтверждено</span>
            <div className="text-3xl font-extrabold text-emerald-400 mt-1">{confirmedCount}</div>
          </div>
          <div className="p-4 rounded-xl border border-amber-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Запланировано</span>
            <div className="text-3xl font-extrabold text-amber-400 mt-1">{plannedCount}</div>
          </div>
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Типы</span>
            <div className="text-3xl font-extrabold text-cyan-400 mt-1">{typesCount}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5">
          <h3 className="text-sm font-bold text-white">Новое событие</h3>
          <p className="text-xs text-slate-400 mt-0.5">Встреча или дежурство.</p>

          <form onSubmit={handleCreateEvent} className="mt-4 space-y-4">
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
                Название
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Например: консультация с докторантами"
                className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                required
              />
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
                Тип
              </label>
              <select
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-400"
              >
                <option value="Встреча">Встреча</option>
                <option value="Дежурство">Дежурство</option>
                <option value="Семинар">Семинар</option>
                <option value="Аттестация">Аттестация</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
                Дата и время
              </label>
              <input
                type="text"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                placeholder="2026-08-25 15:00"
                className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-400"
                required
              />
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
                Получатели
              </label>
              <select
                value={participants}
                onChange={(e) => setParticipants(e.target.value)}
                className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-400"
              >
                <option value="Сотрудники кафедры">Сотрудники кафедры</option>
                <option value="Ответственный преподаватель">Ответственный преподаватель</option>
                <option value="Докторанты 1–3 курса">Докторанты 1–3 курса</option>
                <option value="Все участники кафедры">Все участники кафедры</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={submitting || !title.trim()}
              className="w-full py-2.5 px-4 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold text-xs transition-all shadow active:scale-95 disabled:opacity-50"
            >
              {submitting ? 'Создание...' : 'Создать и пригласить'}
            </button>
          </form>
        </div>

        <div className="lg:col-span-7 rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5">
          <h3 className="text-sm font-bold text-white">События кафедры</h3>
          <p className="text-xs text-slate-400 mt-0.5">Статус подтверждения можно менять.</p>

          <div className="mt-4 space-y-3">
            {events.map((evt) => (
              <div
                key={evt.id}
                className="p-4 rounded-xl bg-[#061420] border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              >
                <div className="min-w-0 pr-2">
                  <h4 className="text-xs font-bold text-white truncate">{evt.title}</h4>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {evt.event_code} · {evt.event_date} · {evt.participants}
                  </p>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <span
                    className={`text-[10px] font-mono px-2.5 py-1 rounded font-bold ${
                      evt.status === 'CONFIRMED'
                        ? 'bg-emerald-950 text-emerald-300 border border-emerald-800'
                        : 'bg-amber-950 text-amber-300 border border-amber-800'
                    }`}
                  >
                    {evt.status}
                  </span>

                  <button
                    type="button"
                    onClick={() => handleToggleConfirm(evt.id)}
                    className="px-3 py-1 rounded-lg border border-cyan-500/30 bg-[#0c2336] hover:bg-[#112f48] text-cyan-300 text-xs font-semibold transition-all active:scale-95"
                  >
                    {evt.status === 'CONFIRMED' ? 'Вернуть в план' : 'Подтвердить'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl bg-[#081a27] border border-cyan-500/40 text-cyan-300 text-xs font-semibold shadow-2xl animate-fade-in">
          <CheckIcon className="w-4 h-4 text-cyan-400" />
          <span>{toastMessage}</span>
        </div>
      )}
    </div>
  );
};
